// lib/dokumente/briefApi.ts
// #539 (Ausbau): Datenzugriffe für den Blanko-Brief — Empfänger suchen/laden, Brief-Vorlagen,
// Vorschlag für den Zielordner und Ablage des PDFs in Drive/Akte.
import { supabase } from '../supabase/client';
import { aktuelleZeile, type EinheitEigZeitraum } from '../eigentuemer/einheitEigentum';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { ladeDateiHoch, type BaumKnoten } from './aktenschrankApi';
import { anschriftText, briefWerte, empfaengerWerte, type EigentuemerDaten, type EigentumDaten, type EinheitDaten, type MieterDaten, type ObjektDaten } from './briefDaten';
import { istWohnend } from '../dashboard/leerstand';

export type EmpfaengerArt = 'mieter' | 'eigentuemer' | 'kontakt' | 'dienstleister';
export interface EmpfaengerTreffer { art: EmpfaengerArt; id: string; name: string; sub: string }
export interface EmpfaengerDaten {
  art: EmpfaengerArt; id: string; name: string;
  anschrift: string;
  werte: Record<string, string>;
  objektId: string | null;
  /** Ordnername-Anfang für den Ablage-Vorschlag („Berg, Anna“) */
  ordnerName: string | null;
}

const muster = (q: string) => `%${q.replace(/[%_\\]/g, (c) => `\\${c}`)}%`;
const nameVon = (x: { vorname?: string | null; nachname?: string | null; firma_name?: string | null }) =>
  [x.vorname, x.nachname].filter(Boolean).join(' ').trim() || (x.firma_name ?? '').trim() || 'Ohne Namen';

/** Mieter, Eigentümer und Kontakte nach Name/Firma (je bis 8 Treffer). */
export async function sucheEmpfaenger(q: string): Promise<EmpfaengerTreffer[]> {
  const s = q.trim();
  if (s.length < 2) return [];
  const p = muster(s);
  const oder = `vorname.ilike.${p},nachname.ilike.${p},firma_name.ilike.${p}`;
  const dlOder = `name.ilike.${p},ansprechpartner.ilike.${p}`;
  const [m, e, k, d] = await Promise.all([
    supabase.from('mieter').select('id, vorname, nachname, firma_name, status, einheit, objekt:objekt_id(objektnummer, strasse, hausnummer)').or(oder).limit(8),
    supabase.from('eigentuemer').select('id, vorname, nachname, firma_name, stadt').or(oder).limit(8),
    supabase.from('kontakte').select('id, vorname, nachname, firma_name, stadt, kontaktart').or(oder).limit(8),
    supabase.from('dienstleister').select('id, name, ansprechpartner, stadt').or(dlOder).limit(8),
  ]);
  const raus: EmpfaengerTreffer[] = [];
  for (const x of (m.data ?? []) as any[]) {
    const o = x.objekt;
    raus.push({ art: 'mieter', id: x.id, name: nameVon(x), sub: ['Mieter', o ? `${o.objektnummer ?? ''} ${[o.strasse, o.hausnummer].filter(Boolean).join(' ')}`.trim() : '', x.einheit, x.status].filter(Boolean).join(' · ') });
  }
  for (const x of (e.data ?? []) as any[]) raus.push({ art: 'eigentuemer', id: x.id, name: eigentuemerAnzeigename(x) || nameVon(x), sub: ['Eigentümer', x.stadt].filter(Boolean).join(' · ') });
  for (const x of (k.data ?? []) as any[]) raus.push({ art: 'kontakt', id: x.id, name: nameVon(x), sub: ['Kontakt', x.kontaktart, x.stadt].filter(Boolean).join(' · ') });
  for (const x of (d.data ?? []) as any[]) raus.push({ art: 'dienstleister', id: x.id, name: (x.name ?? '').trim() || 'Ohne Namen', sub: ['Dienstleister', x.ansprechpartner, x.stadt].filter(Boolean).join(' · ') });
  return raus;
}

async function eigentuemerZuObjekt(objektId: string | null, einheitId: string | null): Promise<EigentuemerDaten | null> {
  let eigId: string | null = null;
  if (einheitId) {
    const ee = (await supabase.from('einheit_eigentuemer').select('eigentuemer_id, mea, hausgeld_vorschuss, vertrag_ab, vertrag_bis').eq('einheit_id', einheitId)).data ?? [];
    eigId = aktuelleZeile(ee as ({ eigentuemer_id: string } & EinheitEigZeitraum)[], new Date().toISOString().slice(0, 10))?.eigentuemer_id ?? null;
  }
  if (!eigId && objektId) {
    eigId = ((await supabase.from('objekt_eigentuemer').select('eigentuemer_id').eq('objekt_id', objektId).limit(1)).data ?? [])[0]?.eigentuemer_id ?? null;
  }
  if (!eigId) return null;
  return (await supabase.from('eigentuemer')
    .select(EIG_SPALTEN).eq('id', eigId).maybeSingle()).data ?? null;
}

const EIG_SPALTEN = 'anrede, vorname, nachname, firma_name, strasse, hausnummer, plz, stadt, zustell_strasse, zustell_hausnummer, zustell_plz, zustell_stadt, email, telefon, mobil, bankverbindung, bic, kontoinhaber, glaeubiger_id, sepa_mandatsreferenz, kundennummer, vertragsstart, vertragsende';

/** #590: Eigentum eines Eigentümers im Objekt – laufende Einheiten mit MEA, Hausgeld und Rücklage. */
async function eigentumImObjekt(eigId: string, objektId: string | null): Promise<EigentumDaten | null> {
  if (!objektId) return null;
  const { data } = await supabase.from('einheit_eigentuemer')
    .select('mea, hausgeld_vorschuss, ruecklage_vorschuss, vertrag_ab, vertrag_bis, objekt_einheiten!inner(bezeichnung, objekt_id)')
    .eq('eigentuemer_id', eigId).eq('objekt_einheiten.objekt_id', objektId);
  const heute = new Date().toISOString().slice(0, 10);
  const zeilen = ((data ?? []) as unknown as { mea: number | null; hausgeld_vorschuss: number | null; ruecklage_vorschuss: number | null; vertrag_ab: string | null; vertrag_bis: string | null; objekt_einheiten: { bezeichnung: string | null } }[])
    .filter((z) => !z.vertrag_bis || z.vertrag_bis.slice(0, 10) >= heute);
  if (!zeilen.length) return null;
  const summe = (f: (z: typeof zeilen[number]) => number | null) => zeilen.reduce((s, z) => s + (Number(f(z)) || 0), 0);
  return {
    einheiten: zeilen.map((z) => (z.objekt_einheiten.bezeichnung ?? '').trim()).filter(Boolean),
    mea: summe((z) => z.mea), hausgeld: summe((z) => z.hausgeld_vorschuss), ruecklage: summe((z) => z.ruecklage_vorschuss),
    ab: zeilen.map((z) => z.vertrag_ab).filter(Boolean).sort()[0] ?? null,
  };
}

/** #592: Einheit des Mieters (Bezeichnung, Etage) und alle, die dort laufend wohnen (Mitmieter eingeschlossen). */
async function einheitZuMieter(einheitId: string | null, eigener: string): Promise<EinheitDaten | null> {
  if (!einheitId) return eigener ? { personen: [eigener] } : null;
  const [einheit, mitbewohner] = await Promise.all([
    supabase.from('objekt_einheiten').select('bezeichnung, etage, wohnflaeche, zimmer, badezimmer, typ').eq('id', einheitId).maybeSingle(),
    supabase.from('mieter').select('vorname, nachname, firma_name, status, mietbeginn').eq('einheit_id', einheitId).order('mietbeginn'),
  ]);
  const heute = new Date().toISOString().slice(0, 10);
  const personen = ((mitbewohner.data ?? []) as { vorname: string | null; nachname: string | null; firma_name: string | null; status: string | null; mietbeginn: string | null }[])
    .filter((p) => istWohnend(p.status) || (p.mietbeginn ?? '') > heute)
    .map((p) => (p.firma_name ?? '').trim() || [p.vorname, p.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' '))
    .filter(Boolean);
  const e = einheit.data as Omit<EinheitDaten, 'personen'> | null;
  return { ...(e ?? {}), personen: [...new Set(personen.length ? personen : [eigener].filter(Boolean))] };
}

const OBJEKT_SPALTEN = 'id, bezeichnung, strasse, hausnummer, plz, stadt, objektnummer, objekttyp, baujahr, heizungsart, wohnflaeche, verwaltungsart, energieklasse';

/** Anschrift und Platzhalter-Werte des gewählten Empfängers. */
export async function ladeEmpfaenger(t: EmpfaengerTreffer): Promise<EmpfaengerDaten> {
  const heute = new Date();
  if (t.art === 'mieter') {
    const { data: m, error } = await supabase.from('mieter')
      .select('id, anrede, vorname, nachname, firma_name, email, telefon, mobil, geburtsdatum, mietbeginn, mietende, kaltmiete, nebenkosten_vorauszahlung, heizkosten, kaution, kundennummer, objekt_id, einheit_id, einheit, weitere_kosten, zahlungsweise, bankverbindung, mieter_iban, mieter_kontoinhaber, sepa_mandatsreferenz, sepa_mandatsdatum, anzahl_bewohner')
      .eq('id', t.id).single();
    if (error || !m) throw new Error('Mieter nicht gefunden');
    const objekt = m.objekt_id ? (await supabase.from('objekte').select(OBJEKT_SPALTEN).eq('id', m.objekt_id).maybeSingle()).data as ObjektDaten | null : null;
    const [eig, einheit] = await Promise.all([eigentuemerZuObjekt(m.objekt_id, m.einheit_id), einheitZuMieter(m.einheit_id, nameVon(m))]);
    return {
      art: 'mieter', id: m.id, name: nameVon(m), objektId: m.objekt_id ?? null,
      anschrift: anschriftText({ anrede: m.anrede, vorname: m.vorname, nachname: m.nachname, firma: m.firma_name, strasse: objekt?.strasse, hausnummer: objekt?.hausnummer, plz: objekt?.plz, ort: objekt?.stadt }),
      werte: { ...briefWerte({ mieter: m as MieterDaten, objekt, eigentuemer: eig, einheit, heute }), ...empfaengerWerte({ anrede: m.anrede, vorname: m.vorname, nachname: m.nachname, firma: m.firma_name }) },
      ordnerName: [m.nachname, m.vorname].filter(Boolean).join(', ') || m.firma_name || null,
    };
  }
  if (t.art === 'eigentuemer') {
    const { data: e, error } = await supabase.from('eigentuemer')
      .select('id, zustell_co, anrede, vorname, nachname, firma_name, strasse, hausnummer, plz, stadt, zustell_strasse, zustell_hausnummer, zustell_plz, zustell_stadt, email, telefon, mobil, bankverbindung, bic, kontoinhaber, glaeubiger_id, sepa_mandatsreferenz, kundennummer, vertragsstart, vertragsende')
      .eq('id', t.id).single();
    if (error || !e) throw new Error('Eigentümer nicht gefunden');
    const zustell = !!(e.zustell_strasse || e.zustell_plz);
    const objektId = ((await supabase.from('objekt_eigentuemer').select('objekt_id').eq('eigentuemer_id', e.id).limit(1)).data ?? [])[0]?.objekt_id ?? null;
    const objekt = objektId ? (await supabase.from('objekte').select(OBJEKT_SPALTEN).eq('id', objektId).maybeSingle()).data as ObjektDaten | null : null;
    return {
      art: 'eigentuemer', id: e.id, name: eigentuemerAnzeigename(e) || nameVon(e), objektId,
      anschrift: anschriftText({
        anrede: e.anrede, vorname: e.vorname, nachname: e.nachname, firma: e.firma_name, co: zustell ? e.zustell_co : null,
        strasse: zustell ? e.zustell_strasse : e.strasse, hausnummer: zustell ? e.zustell_hausnummer : e.hausnummer,
        plz: zustell ? e.zustell_plz : e.plz, ort: zustell ? e.zustell_stadt : e.stadt,
      }),
      werte: { ...briefWerte({ objekt, eigentuemer: e, eigentum: await eigentumImObjekt(e.id, objektId), heute }), ...empfaengerWerte({ anrede: e.anrede, vorname: e.vorname, nachname: e.nachname, firma: e.firma_name }) },
      ordnerName: e.nachname ? [e.nachname, e.vorname].filter(Boolean).join(', ') : e.firma_name ?? null,
    };
  }
  if (t.art === 'dienstleister') {
    const { data: d, error } = await supabase.from('dienstleister').select('id, name, ansprechpartner, strasse, hausnummer, plz, stadt').eq('id', t.id).single();
    if (error || !d) throw new Error('Dienstleister nicht gefunden');
    return {
      art: 'dienstleister', id: d.id, name: (d.name ?? '').trim() || 'Ohne Namen', objektId: null, ordnerName: null,
      anschrift: anschriftText({ firma: d.name, zusatz: d.ansprechpartner ? `z. Hd. ${d.ansprechpartner}` : null, strasse: d.strasse, hausnummer: d.hausnummer, plz: d.plz, ort: d.stadt }),
      werte: { ...briefWerte({ heute }), ...empfaengerWerte({ firma: d.name }) },
    };
  }
  const { data: k, error } = await supabase.from('kontakte').select('id, anrede, vorname, nachname, firma_name, strasse, hausnummer, plz, stadt').eq('id', t.id).single();
  if (error || !k) throw new Error('Kontakt nicht gefunden');
  return {
    art: 'kontakt', id: k.id, name: nameVon(k), objektId: null,
    anschrift: anschriftText({ anrede: k.anrede, vorname: k.vorname, nachname: k.nachname, firma: k.firma_name, strasse: k.strasse, hausnummer: k.hausnummer, plz: k.plz, ort: k.stadt }),
    werte: { ...briefWerte({ heute }), ...empfaengerWerte({ anrede: k.anrede, vorname: k.vorname, nachname: k.nachname, firma: k.firma_name }) }, ordnerName: null,
  };
}

export interface BriefVorlageKurz { id: string; name: string; kategorie: string; html_template: string }

export async function ladeBriefVorlagen(): Promise<BriefVorlageKurz[]> {
  const { data, error } = await supabase.from('dokument_vorlagen').select('id, name, kategorie, html_template').eq('aktiv', true).order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as BriefVorlageKurz[];
}

/**
 * Ablage-Vorschlag ohne Nebenwirkung (kein Ordner wird angelegt): Personenordner des Empfängers
 * („Berg, Anna – W03 …“) im Objektbaum, sonst der Sammelordner (01 Verwaltung/Mieter bzw. /Eigentümer).
 */
export function vorschlagOrdner(baum: readonly BaumKnoten[], art: EmpfaengerArt | null, ordnerName: string | null): BaumKnoten | null {
  const alle: BaumKnoten[] = [];
  const lauf = (l: readonly BaumKnoten[]) => l.forEach((k) => { alle.push(k); lauf(k.kinder); });
  lauf(baum);
  const sammel = art === 'mieter' ? '01 Verwaltung/Mieter' : art === 'eigentuemer' ? '01 Verwaltung/Eigentümer' : null;
  if (ordnerName) {
    const n = ordnerName.toLowerCase();
    const person = alle.find((k) => k.name.toLowerCase().startsWith(n) && (!sammel || k.pfad.includes(sammel.split('/')[1])));
    if (person) return person;
  }
  return sammel ? alle.find((k) => k.pfad === sammel) ?? null : null;
}

/** PDF in den Drive-Ordner laden und am Dokument den Empfänger vermerken. */
export async function legeBriefAb(pdf: Blob, dateiname: string, ordnerId: string, bezug: { art: EmpfaengerArt; id: string } | null, titel: string): Promise<void> {
  const datei = new File([pdf], dateiname, { type: 'application/pdf' });
  await ladeDateiHoch(ordnerId, datei, {
    titel,
    mieterId: bezug?.art === 'mieter' ? bezug.id : undefined,
    eigentuemerId: bezug?.art === 'eigentuemer' ? bezug.id : undefined,
  });
}
